import apiService from '../index';
import fileService from '../utils/fileService';

class HeadToHeadManager {
  constructor() {
    this.apiService = apiService;
    this.config = this.apiService.config;
  }

  // New method to fetch statistics for multiple fixtures and calculate averages
  async fetchAverageStatistics(fixtureIDs) {
    try {
      if (!fixtureIDs || !Array.isArray(fixtureIDs) || fixtureIDs.length === 0) {
        console.error('Error: No fixture IDs provided for statistics');
        return {
          status: 'error',
          message: 'No fixture IDs provided',
          endpoint: 'fixtures/statistics'
        };
      }

      console.log(`Fetching statistics for ${fixtureIDs.length} fixtures...`);
      
      // Fetch statistics for each fixture
      const statsPromises = fixtureIDs.map(fixtureID => 
        this.apiService.getStatisticsH2H(fixtureID)
      );
      
      const statsResponses = await Promise.all(statsPromises);
      
      // Filter out failed responses
      const validResponses = statsResponses.filter(
        response => response.status !== 'error' && response.data && 
        response.data.response && response.data.response.length >= 2
      );
      
      if (validResponses.length === 0) {
        console.error('Error: No valid statistics data obtained');
        return {
          status: 'error',
          message: 'No valid statistics data',
          endpoint: 'fixtures/statistics'
        };
      }
      
      // Process and average the statistics
      const aggregatedStats = this.calculateAverageStatistics(validResponses);
      
      return {
        status: 'success',
        data: {
          response: aggregatedStats,
          fixturesCount: validResponses.length
        },
        endpoint: 'fixtures/statistics',
        params: { fixtureIDs },
        fromCache: statsResponses.some(r => r.fromCache)
      };
    } catch (error) {
      console.error('Error fetching average statistics:', error);
      return {
        status: 'error',
        message: error.message,
        endpoint: 'fixtures/statistics'
      };
    }
  }
  
  // Helper method to calculate average statistics from multiple responses
  calculateAverageStatistics(statsResponses) {
    // Extract teams from the first response to maintain consistency
    const team1 = statsResponses[0].data.response[0].team;
    const team2 = statsResponses[0].data.response[1].team;
    
    // Initialize aggregated stats with team info but empty statistics
    const aggregatedStats = [
      { team: team1, statistics: [] },
      { team: team2, statistics: [] }
    ];
    
    // Process each type of statistic
    statsResponses.forEach(response => {
      const responseStats1 = response.data.response[0].statistics || [];
      const responseStats2 = response.data.response[1].statistics || [];
      
      // Process each statistic type for the first team
      responseStats1.forEach((stat, index) => {
        if (!stat || !stat.type) return;
        
        const existingStat = aggregatedStats[0].statistics.find(s => s.type === stat.type);
        
        if (existingStat) {
          // If the stat already exists, add the value for averaging later
          if (stat.value !== null && stat.value !== '') {
            // Try to parse the value as a number
            const numValue = parseFloat(stat.value);
            if (!isNaN(numValue)) {
              existingStat.values.push(numValue);
            } else if (typeof stat.value === 'string' && stat.value.includes('%')) {
              // Handle percentage values
              const percentValue = parseFloat(stat.value.replace('%', ''));
              if (!isNaN(percentValue)) {
                existingStat.values.push(percentValue);
              }
            } else {
              existingStat.values.push(stat.value);
            }
          }
        } else {
          // If the stat doesn't exist yet, create it
          const values = [];
          if (stat.value !== null && stat.value !== '') {
            const numValue = parseFloat(stat.value);
            if (!isNaN(numValue)) {
              values.push(numValue);
            } else if (typeof stat.value === 'string' && stat.value.includes('%')) {
              const percentValue = parseFloat(stat.value.replace('%', ''));
              if (!isNaN(percentValue)) {
                values.push(percentValue);
              }
            } else {
              values.push(stat.value);
            }
          }
          
          aggregatedStats[0].statistics.push({
            type: stat.type,
            values: values,
            value: null // Will be calculated later
          });
        }
      });
      
      // Process each statistic type for the second team
      responseStats2.forEach((stat, index) => {
        if (!stat || !stat.type) return;
        
        const existingStat = aggregatedStats[1].statistics.find(s => s.type === stat.type);
        
        if (existingStat) {
          // If the stat already exists, add the value for averaging later
          if (stat.value !== null && stat.value !== '') {
            // Try to parse the value as a number
            const numValue = parseFloat(stat.value);
            if (!isNaN(numValue)) {
              existingStat.values.push(numValue);
            } else if (typeof stat.value === 'string' && stat.value.includes('%')) {
              // Handle percentage values
              const percentValue = parseFloat(stat.value.replace('%', ''));
              if (!isNaN(percentValue)) {
                existingStat.values.push(percentValue);
              }
            } else {
              existingStat.values.push(stat.value);
            }
          }
        } else {
          // If the stat doesn't exist yet, create it
          const values = [];
          if (stat.value !== null && stat.value !== '') {
            const numValue = parseFloat(stat.value);
            if (!isNaN(numValue)) {
              values.push(numValue);
            } else if (typeof stat.value === 'string' && stat.value.includes('%')) {
              const percentValue = parseFloat(stat.value.replace('%', ''));
              if (!isNaN(percentValue)) {
                values.push(percentValue);
              }
            } else {
              values.push(stat.value);
            }
          }
          
          aggregatedStats[1].statistics.push({
            type: stat.type,
            values: values,
            value: null // Will be calculated later
          });
        }
      });
    });
    
    // Calculate averages for each statistic
    aggregatedStats.forEach(teamStats => {
      teamStats.statistics.forEach(stat => {
        if (stat.values.length > 0) {
          if (typeof stat.values[0] === 'number') {
            // Calculate average for numeric values
            const sum = stat.values.reduce((acc, val) => acc + val, 0);
            const avg = sum / stat.values.length;
            
            // Format based on the type of statistic
            if (stat.type.includes('possession') || stat.type.includes('passes_accuracy')) {
              stat.value = `${Math.round(avg)}%`;
            } else {
              // Round to one decimal place for most stats
              stat.value = Math.round(avg * 10) / 10;
            }
          } else {
            // For non-numeric values, just use the most frequent one
            const valueCounts = {};
            stat.values.forEach(val => {
              valueCounts[val] = (valueCounts[val] || 0) + 1;
            });
            
            let maxCount = 0;
            let mostFrequentValue = null;
            
            Object.entries(valueCounts).forEach(([val, count]) => {
              if (count > maxCount) {
                maxCount = count;
                mostFrequentValue = val;
              }
            });
            
            stat.value = mostFrequentValue;
          }
        } else {
          stat.value = null;
        }
        
        // Remove the values array from the final output
        delete stat.values;
      });
    });
    
    return aggregatedStats;
  }

  async fetchStatisticsH2H(fixtureID) {
    try {
      
      const response = await this.apiService.getStatisticsH2H(fixtureID);
      
      if (response.status === 'error') {
        console.error('Error fetching odds:', response.message);
        return response;
      }
      
      return {
        status: 'success',
        data: response.data,
        endpoint: 'fixtures/statistics',
        params: { fixtureID },
        fromCache: response.fromCache
      };
    } catch (error) {
      console.error('Error in h2hManager:', error);
      return {
        status: 'error',
        message: error.message,
        endpoint: 'fixtures/statistics'
      };
    }
  }

  async fetchHeadToHeadMatches(team1, team2, fromDate, toDate) {
    try {
      
      const h2hParam = `${team1}-${team2}`;

      const response = await this.apiService.getHeadToHeadMatches(h2hParam, fromDate, toDate);
      
      if (response.status === 'error') {
        console.error('Error fetching odds:', response.message);
        return response;
      }
      
      return {
        status: 'success',
        data: response.data,
        endpoint: 'fixtures/statistics',
        params: { h2h: h2hParam, from: fromDate, to: toDate },
        fromCache: response.fromCache
      };
    } catch (error) {
      console.error('Error in h2hManager:', error);
      return {
        status: 'error',
        message: error.message,
        endpoint: 'fixtures/statistics'
      };
    }
  }

  clearHeadToHeadCache(team1, team2) {
    try {
      if (!team1 || !team2) {
        console.error('Ошибка: не указаны ID команд для очистки кэша очных встреч');
        return {
          status: 'error',
          message: 'Не указаны ID команд для очистки кэша',
          endpoint: 'headToHead'
        };
      }
      
      // Формируем параметр h2h в строковом виде "team1-team2" для API
      const h2hParam = `${team1}-${team2}`;
      console.log(`Clearing head to head cache for teams ${h2hParam}...`);
      
      // Удаляем данные из специализированного кэша headToHead
      const success = this.apiService.config.clearCacheItem('headToHead', h2hParam);
      
      // Удаляем данные также из общего apiCache если они есть
      const apiCache = this.apiService.config.config.apiCache || {};
      const apiCacheKeys = Object.keys(apiCache).filter(key => 
        key.includes('/fixtures/headtohead') && key.includes(h2hParam)
      );
      
      apiCacheKeys.forEach(key => {
        this.apiService.config.clearCacheItem('apiCache', key);
      });
      
      // Удаляем файл с данными H2H
      const fileSuccess = fileService.clearH2HFile(team1, team2);
      
      return {
        status: (success || fileSuccess) ? 'success' : 'error',
        message: (success || fileSuccess) ? 'Кэш успешно очищен' : 'Ошибка очистки кэша',
        endpoint: 'headToHead'
      };
    } catch (error) {
      console.error('Error clearing head to head cache:', error);
      return {
        status: 'error',
        message: error.message,
        endpoint: 'headToHead'
      };
    }
  }

  clearAllHeadToHeadCache() {
    try {
      console.log('Clearing all head to head cache...');
      
      // Удаляем все данные из специализированного кэша headToHead
      const success = this.apiService.config.clearCacheItem('headToHead');
      
      // Удаляем связанные данные из общего apiCache
      const apiCache = this.apiService.config.config.apiCache || {};
      const h2hKeys = Object.keys(apiCache).filter(key => 
        key.includes('/fixtures/headtohead')
      );
      
      h2hKeys.forEach(key => {
        this.apiService.config.clearCacheItem('apiCache', key);
      });
      
      // Удаляем все файлы H2H
      const fileSuccess = fileService.clearAllH2HFiles();
      
      return {
        status: (success || fileSuccess) ? 'success' : 'error',
        message: (success || fileSuccess) ? 'Весь кэш очных встреч успешно очищен' : 'Ошибка очистки кэша очных встреч',
        endpoint: 'headToHead'
      };
    } catch (error) {
      console.error('Error clearing all head to head cache:', error);
      return {
        status: 'error',
        message: error.message,
        endpoint: 'headToHead'
      };
    }
  }
  
  getH2HFilesList() {
    return fileService.getH2HFilesList();
  }

  // New method to fetch team recent matches
  async fetchTeamRecentMatches(teamId, limit = 5) {
    try {
      if (!teamId) {
        console.error('Error: No team ID provided');
        return {
          status: 'error',
          message: 'No team ID provided',
          endpoint: 'fixtures/team'
        };
      }

      console.log(`Fetching recent matches for team ${teamId}...`);
      
      // Get the date range - last 3 months
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setMonth(fromDate.getMonth() - 3);
      
      // Format dates as YYYY-MM-DD
      const from = fromDate.toISOString().split('T')[0];
      const to = toDate.toISOString().split('T')[0];
      
      // Fetch team fixtures
      const response = await this.apiService.getTeamFixtures(teamId, from, to);
      
      if (response.status === 'error') {
        console.error('Error fetching team fixtures:', response.message);
        return response;
      }
      
      // Sort by date (most recent first) and take the specified limit
      const fixtures = response.data.response || [];
      const recentFixtures = fixtures
        .filter(fixture => fixture.fixture.status.short === "FT")  // Only finished matches
        .sort((a, b) => new Date(b.fixture.date) - new Date(a.fixture.date))
        .slice(0, limit);
        
      return {
        status: 'success',
        data: {
          response: recentFixtures
        },
        endpoint: 'fixtures/team',
        params: { team: teamId, from, to },
        fromCache: response.fromCache
      };
    } catch (error) {
      console.error('Error fetching team recent matches:', error);
      return {
        status: 'error',
        message: error.message,
        endpoint: 'fixtures/team'
      };
    }
  }

  // Method to fetch team statistics from recent matches
  async fetchTeamRecentStats(teamId, limit = 5) {
    try {
      // First get the recent matches
      const recentMatchesResponse = await this.fetchTeamRecentMatches(teamId, limit);
      
      if (recentMatchesResponse.status !== 'success' || 
          !recentMatchesResponse.data.response || 
          recentMatchesResponse.data.response.length === 0) {
        return {
          status: 'error',
          message: 'No recent matches found for team',
          endpoint: 'fixtures/statistics'
        };
      }
      
      // Extract fixture IDs
      const fixtureIDs = recentMatchesResponse.data.response.map(match => match.fixture.id);
      console.log(`Fetching statistics for ${fixtureIDs.length} recent team matches...`);
      
      // Fetch statistics for each fixture
      const statsPromises = fixtureIDs.map(fixtureID => 
        this.apiService.getStatisticsH2H(fixtureID)
      );
      
      const statsResponses = await Promise.all(statsPromises);
      
      // Filter out failed responses
      const validResponses = statsResponses.filter(
        response => response.status !== 'error' && response.data && 
        response.data.response && response.data.response.length >= 2
      );
      
      if (validResponses.length === 0) {
        console.error('Error: No valid statistics data obtained');
        return {
          status: 'error',
          message: 'No valid statistics data for recent matches',
          endpoint: 'fixtures/statistics'
        };
      }
      
      // Find the team in each match's statistics
      const teamStats = [];
      
      validResponses.forEach(response => {
        const statsData = response.data.response;
        if (!statsData || statsData.length < 2) return;
        
        // Find which team in the stats is our target team
        const teamStat = statsData.find(stat => stat.team.id === teamId);
        if (teamStat) {
          teamStats.push(teamStat);
        }
      });
      
      if (teamStats.length === 0) {
        return {
          status: 'error',
          message: 'Could not find team statistics in match data',
          endpoint: 'fixtures/statistics'
        };
      }
      
      // Aggregate the statistics
      const aggregatedTeamStat = this.calculateTeamAverageStatistics(teamStats);
      
      return {
        status: 'success',
        data: {
          response: aggregatedTeamStat,
          fixturesCount: teamStats.length,
          matchDetails: recentMatchesResponse.data.response
        },
        endpoint: 'fixtures/statistics',
        params: { teamId },
        fromCache: statsResponses.some(r => r.fromCache)
      };
    } catch (error) {
      console.error('Error fetching team recent statistics:', error);
      return {
        status: 'error',
        message: error.message,
        endpoint: 'fixtures/statistics'
      };
    }
  }

  // Method to fetch both teams' recent statistics
  async fetchTeamsRecentStats(team1Id, team2Id, limit = 5) {
    try {
      const team1StatsPromise = this.fetchTeamRecentStats(team1Id, limit);
      const team2StatsPromise = this.fetchTeamRecentStats(team2Id, limit);
      
      const [team1Stats, team2Stats] = await Promise.all([team1StatsPromise, team2StatsPromise]);
      
      if (team1Stats.status !== 'success' || team2Stats.status !== 'success') {
        const errorMsg = `Error: ${team1Stats.status !== 'success' ? team1Stats.message : ''} ${team2Stats.status !== 'success' ? team2Stats.message : ''}`;
        return {
          status: 'error',
          message: errorMsg.trim(),
          endpoint: 'fixtures/statistics'
        };
      }
      
      // Combine the results
      return {
        status: 'success',
        data: {
          response: [
            team1Stats.data.response,
            team2Stats.data.response
          ],
          team1Fixtures: {
            count: team1Stats.data.fixturesCount,
            matches: team1Stats.data.matchDetails
          },
          team2Fixtures: {
            count: team2Stats.data.fixturesCount,
            matches: team2Stats.data.matchDetails
          }
        },
        endpoint: 'fixtures/statistics',
        fromCache: team1Stats.fromCache || team2Stats.fromCache
      };
    } catch (error) {
      console.error('Error fetching teams recent statistics:', error);
      return {
        status: 'error',
        message: error.message,
        endpoint: 'fixtures/statistics'
      };
    }
  }

  // Helper method to calculate average statistics for a team from multiple matches
  calculateTeamAverageStatistics(teamStats) {
    if (!teamStats || teamStats.length === 0) return null;
    
    // Use the team info from the first stat
    const team = teamStats[0].team;
    
    // Initialize aggregated stats with team info but empty statistics
    const aggregatedStat = { 
      team: team, 
      statistics: [] 
    };
    
    // Process each match's stats
    teamStats.forEach(matchStat => {
      const statistics = matchStat.statistics || [];
      
      // Process each statistic type
      statistics.forEach(stat => {
        if (!stat || !stat.type) return;
        
        const existingStat = aggregatedStat.statistics.find(s => s.type === stat.type);
        
        if (existingStat) {
          // If the stat already exists, add the value for averaging later
          if (stat.value !== null && stat.value !== '') {
            // Try to parse the value as a number
            const numValue = parseFloat(stat.value);
            if (!isNaN(numValue)) {
              existingStat.values.push(numValue);
            } else if (typeof stat.value === 'string' && stat.value.includes('%')) {
              // Handle percentage values
              const percentValue = parseFloat(stat.value.replace('%', ''));
              if (!isNaN(percentValue)) {
                existingStat.values.push(percentValue);
              }
            } else {
              existingStat.values.push(stat.value);
            }
          }
        } else {
          // If the stat doesn't exist yet, create it
          const values = [];
          if (stat.value !== null && stat.value !== '') {
            const numValue = parseFloat(stat.value);
            if (!isNaN(numValue)) {
              values.push(numValue);
            } else if (typeof stat.value === 'string' && stat.value.includes('%')) {
              const percentValue = parseFloat(stat.value.replace('%', ''));
              if (!isNaN(percentValue)) {
                values.push(percentValue);
              }
            } else {
              values.push(stat.value);
            }
          }
          
          aggregatedStat.statistics.push({
            type: stat.type,
            values: values,
            value: null // Will be calculated later
          });
        }
      });
    });
    
    // Calculate averages for each statistic
    aggregatedStat.statistics.forEach(stat => {
      if (stat.values.length > 0) {
        if (typeof stat.values[0] === 'number') {
          // Calculate average for numeric values
          const sum = stat.values.reduce((acc, val) => acc + val, 0);
          const avg = sum / stat.values.length;
          
          // Format based on the type of statistic
          if (stat.type.includes('possession') || stat.type.includes('passes_accuracy')) {
            stat.value = `${Math.round(avg)}%`;
          } else {
            // Round to one decimal place for most stats
            stat.value = Math.round(avg * 10) / 10;
          }
        } else {
          // For non-numeric values, just use the most frequent one
          const valueCounts = {};
          stat.values.forEach(val => {
            valueCounts[val] = (valueCounts[val] || 0) + 1;
          });
          
          let maxCount = 0;
          let mostFrequentValue = null;
          
          Object.entries(valueCounts).forEach(([val, count]) => {
            if (count > maxCount) {
              maxCount = count;
              mostFrequentValue = val;
            }
          });
          
          stat.value = mostFrequentValue;
        }
      } else {
        stat.value = null;
      }
      
      // Remove the values array from the final output
      delete stat.values;
    });
    
    return aggregatedStat;
  }
}

export default new HeadToHeadManager(); 